/**
 * Manufacturing Hooks - React hooks for BOM and Production Order management
 */

import {
  createBom,
  createProductionOrder,
  deleteBom,
  getBomById,
  getBoms,
  getProductionOrderById,
  getProductionOrders,
  updateBom,
  updateProductionOrderStatus,
  type Bom,
  type BomFilters,
  type BomListResponse,
  type CreateBomDTO,
  type CreateProductionOrderDTO,
  type ProductionOrder,
  type ProductionOrderFilters,
  type ProductionOrderListResponse,
  type ProductionOrderStatus,
  type UpdateBomDTO,
} from '@/api/manufacturing';
import { useCallback, useEffect, useState } from 'react';

// ============================================
// TYPES
// ============================================

interface UseBomsResult {
  boms: Bom[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseBomResult {
  bom: Bom | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseProductionOrdersResult {
  orders: ProductionOrder[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseProductionOrderResult {
  order: ProductionOrder | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseMutationResult<TArgs extends unknown[]> {
  mutate: (...args: TArgs) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// BOM HOOKS
// ============================================

/**
 * Hook to fetch and manage BOM list
 */
export function useBoms(filters: BomFilters = {}): UseBomsResult {
  const [boms, setBoms] = useState<Bom[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 20, hasMore: false });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filtersKey = JSON.stringify(filters);
  const fetchBoms = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: BomListResponse = await getBoms(filters);
      setBoms(response.data);
      setMeta(response.meta);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch BOMs';
      setError(message);
      console.error('Error fetching BOMs:', err);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  useEffect(() => {
    void fetchBoms();
  }, [fetchBoms]);

  return {
    boms,
    total: meta.total,
    page: meta.page,
    pageSize: meta.pageSize,
    hasMore: meta.hasMore,
    isLoading,
    error,
    refetch: fetchBoms,
  };
}

/**
 * Hook to fetch single BOM by ID
 */
export function useBom(id: string | undefined): UseBomResult {
  const [bom, setBom] = useState<Bom | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBom = useCallback(async () => {
    if (!id) {
      setBom(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getBomById(id);
      setBom(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch BOM';
      setError(message);
      console.error('Error fetching BOM:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchBom();
  }, [fetchBom]);

  return {
    bom,
    isLoading,
    error,
    refetch: fetchBom,
  };
}

/**
 * Hook to create a BOM
 */
export function useCreateBom(): UseMutationResult<[CreateBomDTO]> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (data: CreateBomDTO) => {
    setIsLoading(true);
    setError(null);
    try {
      await createBom(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create BOM';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error };
}

/**
 * Hook to update a BOM
 */
export function useUpdateBom(): UseMutationResult<[string, UpdateBomDTO]> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (id: string, data: UpdateBomDTO) => {
    setIsLoading(true);
    setError(null);
    try {
      await updateBom(id, data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update BOM';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error };
}

/**
 * Hook to delete a BOM
 */
export function useDeleteBom(): UseMutationResult<[string]> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      await deleteBom(id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete BOM';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error };
}

// ============================================
// PRODUCTION ORDER HOOKS
// ============================================

/**
 * Hook to fetch and manage production order list
 */
export function useProductionOrders(
  filters: ProductionOrderFilters = {}
): UseProductionOrdersResult {
  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, pageSize: 20, hasMore: false });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orderFiltersKey = JSON.stringify(filters);
  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response: ProductionOrderListResponse = await getProductionOrders(filters);
      setOrders(response.data);
      setMeta(response.meta);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch production orders';
      setError(message);
      console.error('Error fetching production orders:', err);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderFiltersKey]);

  useEffect(() => {
    void fetchOrders();
  }, [fetchOrders]);

  return {
    orders,
    total: meta.total,
    page: meta.page,
    pageSize: meta.pageSize,
    hasMore: meta.hasMore,
    isLoading,
    error,
    refetch: fetchOrders,
  };
}

/**
 * Hook to fetch single production order by ID
 */
export function useProductionOrder(id: string | undefined): UseProductionOrderResult {
  const [order, setOrder] = useState<ProductionOrder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrder = useCallback(async () => {
    if (!id) {
      setOrder(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const response = await getProductionOrderById(id);
      setOrder(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch production order';
      setError(message);
      console.error('Error fetching production order:', err);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void fetchOrder();
  }, [fetchOrder]);

  return {
    order,
    isLoading,
    error,
    refetch: fetchOrder,
  };
}

/**
 * Hook to create a production order
 */
export function useCreateProductionOrder(): UseMutationResult<[CreateProductionOrderDTO]> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (data: CreateProductionOrderDTO) => {
    setIsLoading(true);
    setError(null);
    try {
      await createProductionOrder(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create production order';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error };
}

/**
 * Hook to update production order status
 */
export function useUpdateProductionOrderStatus(): UseMutationResult<
  [string, ProductionOrderStatus]
> {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutate = useCallback(async (id: string, status: ProductionOrderStatus) => {
    setIsLoading(true);
    setError(null);
    try {
      await updateProductionOrderStatus(id, status);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to update production order status';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutate, isLoading, error };
}
